import React from "react";
import { Link } from "react-router-dom";
import ServiceLayout from "../components/ServiceLayout";

const DailyCorporateMeals = () => {
  const whatsIncluded = [
    {
      title: "Rotating Office Menus",
      description: "Structured menu cycles with Indian regional favourites, lighter options and periodic global cuisines to keep everyday meals varied."
    },
    {
      title: "Dietary Meal Options",
      description: "Vegetarian, Jain, vegan and other dietary requirements can be planned into the programme based on your team's needs."
    },
    {
      title: "Breakfast, Lunch & Snacks",
      description: "Build a workplace food programme around the meal periods your office needs, from breakfast and lunch to evening snacks."
    },
    {
      title: "On-Site Service Support",
      description: "Service staff and buffet support can be planned for offices that need managed meal service rather than simple drop-off delivery."
    },
    {
      title: "Live Food Stations",
      description: "Add live counters such as dosa, chaat or other made-to-order formats where the site and programme are suitable."
    },
    {
      title: "Planned Workplace Delivery",
      description: "Meal schedules are planned around office timings, team size, service format and the practical requirements of your workplace."
    }
  ];

  const perfectFor = [
    "Corporate offices and growing teams",
    "Technology companies and GCCs",
    "Co-working and managed workspaces",
    "BPOs and offices operating in shifts",
    "Educational institutions and campuses",
    "Healthcare and other institutional workplaces"
  ];

  const faqs = [
    {
      question: "Do you provide daily office lunch delivery in Bangalore?",
      answer: "Yes. The Office Meal Co. provides recurring corporate meal programmes for workplaces in Bengaluru, including scheduled office lunches and other meal periods based on the requirement."
    },
    {
      question: "Can you create a rotating corporate meal plan for our employees?",
      answer: "Yes. Menus can be planned as rotating cycles so teams receive variety across the month. The programme can be adjusted around cuisine preferences, dietary requirements, service style and budget."
    },
    {
      question: "Do you provide vegetarian, Jain and vegan office meals?",
      answer: "Yes. Vegetarian, Jain and vegan requirements can be incorporated into the menu plan. Share your team's dietary mix while requesting a proposal so the programme can be designed accordingly."
    },
    {
      question: "Which parts of Bangalore do you serve?",
      answer: "We serve corporate clients across Bengaluru. Our kitchen is in Electronic City, and service feasibility is planned around the office location, meal timing, volume and service format."
    },
    {
      question: "Can you provide staff for serving meals at our office?",
      answer: "Yes. For programmes that require managed service, on-site service staff and buffet support can be included as part of the operating plan."
    },
    {
      question: "How do we get a quote for a daily corporate meal programme?",
      answer: "Share your office location, approximate number of employees, required meal periods, preferred service format and any dietary requirements. We can then prepare a proposal suited to your workplace."
    }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Daily Corporate Meal Services in Bangalore",
      "serviceType": "Daily corporate meals and office lunch service",
      "url": "https://theofficemealco.com/daily-corporate-meals",
      "provider": {
        "@type": "Organization",
        "name": "The Office Meal Co.",
        "url": "https://theofficemealco.com/"
      },
      "areaServed": {
        "@type": "City",
        "name": "Bengaluru"
      },
      "description": "Recurring office meal programmes in Bangalore with rotating menus, dietary options, scheduled workplace delivery and managed service support."
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    }
  ];

  return (
    <ServiceLayout
      title="Daily Corporate Meal Services for Offices in Bangalore"
      subtitle="Recurring breakfast, office lunch and snack programmes for Bengaluru workplaces, with rotating menus, dietary options and service formats tailored to your team."
      heroImage="/images/corporate-meals.png"
      whatsIncluded={whatsIncluded}
      perfectFor={perfectFor}
      seoTitle="Daily Corporate Meals & Office Lunch Bangalore"
      seoDescription="Daily corporate meals and office lunch service in Bangalore. Rotating workplace menus, vegetarian, Jain and vegan options, scheduled delivery and service support."
      canonicalUrl="https://theofficemealco.com/daily-corporate-meals"
      structuredData={structuredData}
    >
      <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
        <div>
          <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
            01 • Office Meal Programmes
          </span>
          <h2 className="font-serif text-3xl text-[#1E3A5F] mb-6">
            Office breakfast, lunch and snack programmes built around your workday
          </h2>
          <p className="text-[#64748B] leading-relaxed mb-6 font-sans">
            A daily office meal programme has to do more than deliver food. It has to arrive at the right time, offer enough variety for people eating it every day, accommodate different dietary preferences and fit the way your workplace actually operates.
          </p>
          <p className="text-[#64748B] leading-relaxed mb-6 font-sans">
            The Office Meal Co. plans recurring corporate meals for Bengaluru workplaces from our kitchen in Electronic City. Depending on the programme, meals can include breakfast, lunch and snacks, with buffet service, packed formats or live counters planned around the site and requirement.
          </p>
          <p className="text-[#64748B] leading-relaxed font-sans">
            For meetings, training days or individually packed requirements, our <Link to="/bento-boxes" className="text-[#1E3A5F] underline underline-offset-4 hover:text-[#CBA365]">corporate bento boxes</Link> can complement the daily meal programme.
          </p>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 bg-[#E8DDCB] rounded-full opacity-20 blur-3xl"></div>
          <img
            src="/images/live-station.png"
            alt="Live food station for a corporate office meal service in Bangalore"
            className="relative w-full h-[350px] object-cover shadow-lg"
            loading="lazy"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-12 border-t border-b border-[#E8DDCB] mb-16">
        <div className="text-center">
          <span className="font-serif text-4xl text-[#1E3A5F] block">28</span>
          <span className="text-sm text-[#64748B] font-sans">Day menu cycle</span>
        </div>
        <div className="text-center">
          <span className="font-serif text-4xl text-[#1E3A5F] block">3</span>
          <span className="text-sm text-[#64748B] font-sans">Core meal periods</span>
        </div>
        <div className="text-center">
          <span className="font-serif text-4xl text-[#1E3A5F] block">50–5K</span>
          <span className="text-sm text-[#64748B] font-sans">Office programme scale</span>
        </div>
        <div className="text-center">
          <span className="font-serif text-4xl text-[#1E3A5F] block">1</span>
          <span className="text-sm text-[#64748B] font-sans">In-house kitchen</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-12 mb-16">
        <div>
          <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
            02 • Meal Planning
          </span>
          <h2 className="font-serif text-3xl text-[#1E3A5F] mb-6">
            Corporate meal plans designed for repeat dining
          </h2>
          <p className="text-[#64748B] leading-relaxed mb-4 font-sans">
            Employees eating at work every day need variety without making the programme difficult to operate. We build menu cycles around familiar Indian meals, regional favourites and selected global formats, while balancing practical service considerations.
          </p>
          <p className="text-[#64748B] leading-relaxed font-sans">
            Dietary requirements can be incorporated during menu planning rather than treated as an afterthought. Tell us the mix of vegetarian, Jain, vegan or other requirements in your team and we can design the proposal around them.
          </p>
        </div>
        <div>
          <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
            03 • Service Formats
          </span>
          <h2 className="font-serif text-3xl text-[#1E3A5F] mb-6">
            From scheduled office lunch delivery to managed meal service
          </h2>
          <p className="text-[#64748B] leading-relaxed mb-4 font-sans">
            Different workplaces need different operating models. A smaller office may need scheduled meal delivery, while a larger workplace may require buffet setup, service staff and a more structured daily dining programme.
          </p>
          <p className="text-[#64748B] leading-relaxed font-sans">
            We can also support special office occasions through our <Link to="/event-catering" className="text-[#1E3A5F] underline underline-offset-4 hover:text-[#CBA365]">corporate event catering</Link> service and ongoing beverage or snack requirements through <Link to="/pantry-management" className="text-[#1E3A5F] underline underline-offset-4 hover:text-[#CBA365]">office pantry management</Link>.
          </p>
        </div>
      </div>

      <div className="bg-[#F3EFE6] p-8 md:p-12 mb-16">
        <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
          04 • Bengaluru Coverage
        </span>
        <h2 className="font-serif text-3xl text-[#1E3A5F] mb-6">
          Daily office meals across Bangalore's major business corridors
        </h2>
        <p className="text-[#64748B] leading-relaxed mb-4 font-sans max-w-4xl">
          Our kitchen is located in Electronic City, Bengaluru. We cater to corporate requirements across the city, including office clusters around Electronic City, HSR Layout, Koramangala, Bellandur, Marathahalli, Whitefield, Outer Ring Road, Bannerghatta Road and other Bengaluru business districts.
        </p>
        <p className="text-[#64748B] leading-relaxed font-sans max-w-4xl">
          Service feasibility depends on your exact office location, meal timing, order volume and service format. Share those details when requesting a quote so we can recommend an operating plan that works for your workplace.
        </p>
      </div>

      <div className="mb-4">
        <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
          05 • Frequently Asked Questions
        </span>
        <h2 className="font-serif text-3xl md:text-4xl text-[#1E3A5F] mb-8">
          Questions about daily corporate meals in Bangalore
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="border border-[#E8DDCB] bg-white p-6">
              <h3 className="font-serif text-xl text-[#1E3A5F] mb-3">{faq.question}</h3>
              <p className="text-[#64748B] leading-relaxed font-sans">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </ServiceLayout>
  );
};

export default DailyCorporateMeals;
